import { Badge, Tooltip, Box, Button, Checkbox, FormControlLabel, Grid, LinearProgress, Paper, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FlagIcon from '@material-ui/icons/Flag';
import HelpIcon from '@material-ui/icons/Help';
import { Alert } from '@material-ui/lab';
import { Listing, ListingCompTable, TraxxButton, TraxxInput } from 'app/components';
import { API } from 'app/services';
import { snakeToTitle, useAsyncTask } from 'app/utils';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { KeywordPills } from './KeywordPill';

const useStyles = makeStyles((theme) => ({
  root: {},
  head: { fontSize: 13, fontWeight: 'bold' },
  paper: { overflowX: 'auto' },
  listing: { boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.15)' },
  resolveButton: { float: 'right', marginTop: theme.spacing(2) },
  inputReason: { paddingLeft: theme.spacing(1), paddingRight: theme.spacing(1) },
  flagged: { color: '#2CC990' },
  unflagged: { color: '#CCCCCC' },
  clickable: { cursor: 'pointer' },
}));

const DEFAULT_HEADER = ['', 'Match', 'Flag', 'Resolved', 'Resolve reason'];
const DOWJONES_HEADER = ['', 'Match', 'Rating', 'Match Score', 'Flag', 'Type', 'Profile', 'Resolved', 'Resolve reason'];
const COMPLYADVANTAGE_HEADER = ['', 'Match', 'Match Type', 'Flag', 'Resolved', 'Resolve reason'];

let mounted = false;
function MatchList(props) {
  const { screening, screening_id, screen_provider_id, newPage, routeType = 'account', onLoad, flagged = 0 } = props;
  const classes = useStyles();
  const [matchList, setMatchList] = useState([]);
  const [filter, setFilter] = useState({ limit: 100, offset: 0, type: [] });
  const loading = useSelector((state) => state.layout.loadingTasks);
  const access = useSelector((state) => state.profile.access);
  const [header, setHeader] = useState(screen_provider_id === 1 ? [...COMPLYADVANTAGE_HEADER] : screen_provider_id === 2 ? [...DOWJONES_HEADER] : [...DEFAULT_HEADER]);
  let [selected, setSelected] = useState([]);
  const [rawList, setRawList] = useState();
  const [attributes, setAttributes] = useState({});
  const [showResolved, setShowResolved] = useState(false);
  const [showUpdated, setShowUpdated] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [noMatchOnly, setNoMatchOnly] = useState(true);
  const [selectAll, setSelectAll] = useState(false);
  const [runLoadMatchList] = useAsyncTask('getMatchList');
  const [runResolveNoMatch] = useAsyncTask('resolveNoMatch');
  const [runFlagMatch] = useAsyncTask('flagMatch');
  const [showConfirmResolve, setShowConfirmResolve] = useState(false);
  const [resolveReason, setResolveReason] = useState();
  const { enqueueSnackbar } = useSnackbar();
  const [flaggedNumber, setFlaggedNumber] = useState(flagged);
  const [blacklist, setBlacklist] = useState([]);

  // ✅ blacklist resolve state
  const [selectedBlacklist, setSelectedBlacklist] = useState([]);
  const [blacklistResolveReason, setBlacklistResolveReason] = useState();
  const [showConfirmBlacklistResolve, setShowConfirmBlacklistResolve] = useState(false);
  const [showBlacklistResolved, setShowBlacklistResolved] = useState(false);
  const [newCount, setNewCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);

  useEffect(() => {
    mounted = true;
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    if (access && access.token) getMatching(filter);
    // eslint-disable-next-line
  }, [access]);

  const onPageChange = (newFilter) => {
    getMatching(newFilter);
  };

  const getMatching = (newFilter = filter) => {
    setMatchList(null);
    runLoadMatchList(async () => {
      const loader = routeType === 'account' ? API.Screening.get_matching_list : API.Admin.list_match;
      const { models, meta } = await loader(screening_id, { ...newFilter, limit: 100, offset: 0 });
      if (mounted) {
        setRawList(models);
        sortListingData(models);
        if (typeof onLoad === 'function') onLoad(models);
        setFilter({ ...meta });
        const newFlagCount = models.filter((m) => m.flag === 'flagged' || m.flag === true).length;

        setFlaggedNumber(newFlagCount);
        const newCount = models.filter((m) => m.rescreen_result === 'new').length;
        const updatedCount = models.filter((m) => m.rescreen_result === 'updated').length;
        setNewCount(newCount);
        setUpdatedCount(updatedCount);
      }
    });
  };

  const getUpdated = (event) => {
    const checked = event.target.checked;
    setShowUpdated(checked);
    if (checked && !showNew) {
      setFilter({ ...filter, type: 'updated' });
      getMatching({ ...filter, type: 'updated' });
    }
    if (checked && showNew) {
      setFilter({ ...filter, type: ['new', 'updated'] });
      getMatching({ ...filter, type: ['new', 'updated'] });
    }
    if (!checked && showNew) {
      setFilter({ ...filter, type: 'new' });
      getMatching({ ...filter, type: 'new' });
    }
    if (!checked && !showNew) {
      getMatching({ ...filter });
      setFilter({ ...filter });
    }
  };

  const getNew = (event) => {
    const checked = event.target.checked;
    setShowNew(checked);
    if (checked && !showUpdated) {
      setFilter({ ...filter, type: 'new' });
      getMatching({ ...filter, type: 'new' });
    }
    if (checked && showUpdated) {
      setFilter({ ...filter, type: ['new', 'updated'] });
      getMatching({ ...filter, type: ['new', 'updated'] });
    }
    if (!checked && showUpdated) {
      setFilter({ ...filter, type: 'updated' });
      getMatching({ ...filter, type: 'updated' });
    }
    if (!checked && !showUpdated) {
      getMatching({ ...filter });
      setFilter({ ...filter });
    }
  };

  const getMatchDetailPath = (screening_id, id) => {
    switch (routeType) {
      case 'admin':
        return `/admin/screenings/${screening_id}/match/${id}/detail`;
      default:
        return `/screenings/${screening_id}/match/${id}/detail`;
    }
  };

  const getVariant = (res) => {
    switch (res) {
      case 'NOT_MATCHED':
        return <CancelIcon color="error" />;
      case 'MATCHED':
        return <CheckCircleIcon style={{ color: '#2cc990' }} />;
      case 'UNKNOWN':
        return <HelpIcon style={{ color: '#f2c94e' }} />;
      default:
        return <HelpIcon style={{ color: '#f2c94e' }} />;
    }
  };

  const toggleMatch = (id, selectedArr, rawListing, resolveOverride) => {
    return () => {
      if (selectedArr.includes(id)) selectedArr.splice(selectedArr.indexOf(id), 1);
      else selectedArr.push(id);
      sortListingData(rawListing, selectedArr, resolveOverride);
    };
  };

  // ✅ toggle blacklist row selection
  const toggleBlacklistSelect = (id) => {
    setSelectedBlacklist((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const resolveBlacklist = () => {
    if (selectedBlacklist.length < 1) return enqueueSnackbar('No selected profile', { variant: 'error' });
    if (!blacklistResolveReason) return enqueueSnackbar('Please state a reason', { variant: 'error' });
    runResolveNoMatch(async () => {
      await API.Users.resolve_matches(screening_id, { resolve_ids: selectedBlacklist, resolve_reason: blacklistResolveReason });
      setShowConfirmBlacklistResolve(false);
      setSelectedBlacklist([]);
      setBlacklistResolveReason('');
      getMatching();
    });
  };

  const sortListingData = (models, selectedArr = selected, resolveOverride = showResolved, attrs = attributes) => {
    let allData = [];
    let newHeader = [...header];
    let newBlacklist = [];

    models.forEach((model) => {
      // hide resolved when checkbox is OFF
      if (!resolveOverride && model.resolved_at) return;

      // show only resolved when checkbox is ON
      if (resolveOverride && !model.resolved_at) return;

      let parsed = {};
      try {
        parsed = JSON.parse(model.match_attributes || '{}');
      } catch (e) {}

      const params = screening?.screen_request?.params || {};
      const paramValues = Object.values(params);

      let arr = [];

      if (screen_provider_id === 1) {
        arr = [
          { value: !!model.resolved_at ? <div></div> : <Checkbox checked={selectedArr.includes(model.id)} onClick={toggleMatch(model.id, selectedArr, models, resolveOverride)} />, type: 'custom', selected: selectedArr.indexOf(model.id) > -1 },
          { label: model.name, type: 'labelwithlink', link: getMatchDetailPath(screening_id, model.id), bottomLabel: model.reference, matchedTerm: model.matched_term, newPage: newPage },
          { label: snakeToTitle(model.match_rating), monoText: true },
          {
            value:
              model.flag === 'flagged' ? (
                <FlagIcon className={clsx(classes.flagged, classes.clickable)} onClick={() => toggleFlag(models, model)} />
              ) : (
                <FlagIcon className={clsx(classes.clickable, classes.unflagged)} onClick={() => toggleFlag(models, model)} />
              ),
            type: 'custom',
          },
          { label: model.match_type },
          { date: model.profile_retrieved_at, type: 'date' },
          { date: model.resolved_at, type: 'date' },
          { label: model.resolved_reason || '', type: 'description' },
        ];
      } else if (screen_provider_id === 2) {
        arr = [
          { value: !!model.resolved_at ? <div></div> : <Checkbox checked={selectedArr.includes(model.id)} onClick={toggleMatch(model.id, selectedArr, models, resolveOverride)} />, type: 'custom', selected: selectedArr.indexOf(model.id) > -1 },
          { label: model.name, type: 'labelwithlink', link: getMatchDetailPath(screening_id, model.id), bottomLabel: model.reference, matchedTerm: model.matched_term, newPage: newPage },
          { label: snakeToTitle(model.match_rating), monoText: true },
          { label: model.score, monoText: true },
          {
            value:
              model.flag === 'flagged' ? (
                <FlagIcon className={clsx(classes.flagged, classes.clickable)} onClick={() => toggleFlag(models, model)} />
              ) : (
                <FlagIcon className={clsx(classes.clickable, classes.unflagged)} onClick={() => toggleFlag(models, model)} />
              ),
            type: 'custom',
          },
          { label: model.match_type },
          { date: model.profile_retrieved_at, type: 'date' },
          { date: model.resolved_at, type: 'date' },
          { label: model.resolved_reason || '', type: 'description' },
        ];
      } else if (screen_provider_id === 4) {
        arr = [
          { value: !!model.resolved_at ? <div></div> : <Checkbox checked={selectedArr.includes(model.id)} onClick={toggleMatch(model.id, selectedArr, models, resolveOverride)} />, type: 'custom', selected: selectedArr.indexOf(model.id) > -1 },
          {
            type: 'custom',
            value: (
              <Tooltip title={model.matched_term || ''}>
                <div style={{ cursor: 'default' }}>
                  {/* ❌ NOT clickable */}
                  <div style={{ fontWeight: 500 }}>{model.name}</div>

                  {/* ✅ ONLY THIS is clickable */}
                  <div
                    style={{
                      fontSize: 12,
                      color: '#1976d2',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => window.open(getMatchDetailPath(screening_id, model.id), '_blank')}>
                    {model.reference}
                  </div>
                </div>
              </Tooltip>
            ),
          },
          {
            value:
              model.flag === 'flagged' ? (
                <FlagIcon className={clsx(classes.flagged, classes.clickable)} onClick={() => toggleFlag(models, model)} />
              ) : (
                <FlagIcon className={clsx(classes.clickable, classes.unflagged)} onClick={() => toggleFlag(models, model)} />
              ),
            type: 'custom',
          },
          { date: model.resolved_at, type: 'date' },
          { label: model.resolved_reason || '', type: 'description' },
        ];
      } else {
        arr = [
          { value: !!model.resolved_at ? <div></div> : <Checkbox checked={selectedArr.includes(model.id)} onClick={toggleMatch(model.id, selectedArr, models, resolveOverride)} />, type: 'custom', selected: selectedArr.indexOf(model.id) > -1 },
          {
            type: 'custom',
            value: (
              <Tooltip title={model.matched_term || ''}>
                <div style={{ cursor: 'default' }}>
                  {/* ❌ NOT clickable */}
                  <div style={{ fontWeight: 500 }}>{model.name}</div>

                  {/* ✅ ONLY THIS is clickable */}
                  <div
                    style={{
                      fontSize: 12,
                      color: '#1976d2',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => window.open(getMatchDetailPath(screening_id, model.id), '_blank')}>
                    {model.reference}
                  </div>
                </div>
              </Tooltip>
            ),
          },
          { label: snakeToTitle(model.match_rating), monoText: true },
          {
            value:
              model.flag === 'flagged' ? (
                <FlagIcon className={clsx(classes.flagged, classes.clickable)} onClick={() => toggleFlag(models, model)} />
              ) : (
                <FlagIcon className={clsx(classes.clickable, classes.unflagged)} onClick={() => toggleFlag(models, model)} />
              ),
            type: 'custom',
          },
          { date: model.resolved_at, type: 'date' },
          { label: model.resolved_reason || '', type: 'description' },
        ];
      }

      if (screen_provider_id == 1 || screen_provider_id == 2 || screen_provider_id == 3) {
        const matchAttributes = JSON.parse(model.match_attributes);
        if (matchAttributes && matchAttributes.length > 0) {
          matchAttributes.forEach((attribute) => {
            if (!newHeader.includes(snakeToTitle(attribute.field))) {
              newHeader.push(snakeToTitle(attribute.field));
              if (!attributes[attribute.field]) attributes[attribute.field] = false;
            }
            arr.push({ type: 'custom', value: getVariant(attribute.result) });
          });
        }
        allData.push(arr);
      } else {
        if (model.match_rating === 'Blacklisted') {
          let parsedBL = {};
          try {
            parsedBL = JSON.parse(model.match_attributes || '{}');
          } catch (e) {}

          const categories = parsedBL.categories?.map((c) => c.shortName || c.name) || [];

          newBlacklist.push({
            id: model.id,
            resolved_at: model.resolved_at,
            name: parsedBL.name || model.name,
            reference: parsedBL.reference || model.reference,
            rating: model.match_rating,
            categories,
            nationality: parsedBL.nationality || '-',
            dob: parsedBL.dob ? new Date(parsedBL.dob).toLocaleDateString() : '-',
            gender: parsedBL.gender || '-',
            countryOfResidence: parsedBL.countryOfResidence || '-',
            idIssuingCountry: parsedBL.idIssuingCountry || '-',
            address: parsedBL.address || '-',
            information: parsedBL.information || '-',
            yob: parsedBL.yob || '-',
            email: parsedBL.email || '-',
            source: model.screen?.db_type,
            flag: model.flag,
          });
          return;
        } else {
          // Handle array format with field, matched, submitted, result
          let matchAttributes = [];
          try {
            const parsed = JSON.parse(model.match_attributes || '[]');
            // Check if it's an array format
            if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].hasOwnProperty('field')) {
              matchAttributes = parsed;
            }
          } catch (e) {
            matchAttributes = [];
          }

          if (matchAttributes.length > 0) {
            matchAttributes.forEach((attribute) => {
              const fieldName = attribute.field;
              const matchedValue = attribute.matched && attribute.matched !== 'null' ? attribute.matched : '-';
              const submittedValue = attribute.submitted && attribute.submitted !== 'null' ? attribute.submitted : null;
              const result = attribute.result;

              if (!newHeader.includes(snakeToTitle(fieldName))) {
                newHeader.push(snakeToTitle(fieldName));
                if (!attributes[fieldName]) attributes[fieldName] = false;
              }

              arr.push({
                type: 'custom',
                value: (
                  <Box display="flex" alignItems="center">
                    <Box component="span" mr={0.5}>
                      {matchedValue}
                    </Box>
                    <Box ml={0.5}>{getVariant(result)}</Box>
                  </Box>
                ),
              });
            });
          }

          allData.push(arr);
        }
      }
    });

    setHeader(newHeader);
    setBlacklist(newBlacklist);
    setMatchList(allData);
  };

  const sortSelected = ({ resolve = showResolved, nomatch = noMatchOnly, attrs = attributes, allSelected = selectAll }, keepSelected = false) => {
    return () => {
      setShowResolved(resolve);
      setNoMatchOnly(nomatch);
      setAttributes(attrs);
      setSelectAll(allSelected);
      if (screen_provider_id == 1 || screen_provider_id == 2 || screen_provider_id == 3) {
        let newSelected = [];
        if (keepSelected) newSelected = selected;
        rawList.forEach((raw) => {
          if (allSelected && !raw.resolved_at) newSelected.push(raw.id);
          if (!(!showResolved && raw.resolved_at)) {
            const matchAttributes = JSON.parse(raw.match_attributes);
            matchAttributes.forEach((attr) => {
              if (attrs[attr.field]) {
                if (nomatch && attr.result === 'MATCHED') if (!newSelected.includes(raw.id)) newSelected.push(raw.id);
              }
            });
          }
        });
        setSelected(newSelected);
        sortListingData(rawList, newSelected, resolve);
      } else {
        let newSelected = keepSelected ? [...selected] : [];
        const hasActiveFilter = Object.values(attrs).some(Boolean);

        rawList.forEach((raw) => {
          if (resolve && !raw.resolved_at) return;
          if (allSelected && !raw.resolved_at && !resolve) {
            if (!newSelected.includes(raw.id)) newSelected.push(raw.id);
          }

          let parsed = {};
          try {
            parsed = JSON.parse(raw.match_attributes || '{}');
          } catch (e) {}

          const params = screening?.screen_request?.params || {};
          const paramValues = Object.values(params);

          if (!hasActiveFilter) return;

          const hasMatch = Object.keys(parsed).some((field) => {
            if (!attrs[field]) return false;
            const value = parsed[field];
            const paramExists = Object.keys(params).includes(field);
            if (!paramExists && (field === 'dob' || field === 'nationality')) return false;
            const isEmpty = value === null || value === undefined || value === '' || value === 'null' || value === 'null-null-null';
            if (Array.isArray(value)) return value.some((v) => paramValues.includes(v));
            if (field === 'nationality') {
              if (isEmpty) return false;
              const parts = value.split(',').map((v) => v.trim());
              return paramValues.some((pv) => parts.includes(pv));
            }
            if (field === 'dob') {
              if (isEmpty) return false;
              const year = value.split('-').pop();
              return paramValues.some((pv) => pv && pv.endsWith(year));
            }
            return paramValues.includes(value);
          });

          if (!hasMatch && nomatch) {
            if (!newSelected.includes(raw.id)) newSelected.push(raw.id);
          }
        });

        setSelected(newSelected);
        sortListingData(rawList, newSelected, resolve, attrs);
      }
    };
  };

  const resolve = () => {
    if (selected.length < 1) return enqueueSnackbar('No selected profile', { variant: 'error' });
    if (!resolveReason) return enqueueSnackbar('Please state a reason', { variant: 'error' });
    runResolveNoMatch(async () => {
      await API.Users.resolve_matches(screening_id, { resolve_ids: selected, resolve_reason: resolveReason });
      setShowConfirmResolve(false);
      getMatching();
      sortSelected(selected.splice(0, selected.length));
    });
  };

  const toggleFlag = (models, model) => {
    const newFlag = model.flag === 'flagged' ? 'not_flagged' : 'flagged';
    let flagCount = 0;
    models.forEach((mod) => {
      if (mod.id === model.id) mod.flag = newFlag;
      if (mod.flag === 'flagged') flagCount++;
    });
    setFlaggedNumber(flagCount);
    sortListingData(models);
    runFlagMatch(async () => {
      await API.Screening.flag_match(screening_id, model.id, { flag: newFlag });
    });
  };

  return (
    <Paper>
      {loading.getMatchList && <LinearProgress size={30} />}
      {!loading.getMatchList && matchList && (
        <Listing title="Match Lists" filter={filter} className={classes.listing} loading={loading.getMatchList} customlimit={100} rowsPerPageArr={[100, 200, 500, 1000]} onPageChange={onPageChange}>
          <Box ml={1} display="flex" p={2}>
            <Box display="inline">
              <FormControlLabel control={<Checkbox onClick={sortSelected({ resolve: !showResolved }, true)} checked={showResolved} />} label="Show resolved" />
              <FormControlLabel
                control={<Checkbox onChange={getNew} checked={showNew} />}
                label={
                  <Badge badgeContent={newCount} color="primary">
                    <span>Show New</span>
                  </Badge>
                }
              />

              <FormControlLabel
                control={<Checkbox onChange={getUpdated} checked={showUpdated} />}
                label={
                  <Badge badgeContent={updatedCount} color="secondary">
                    <span>Show Updated</span>
                  </Badge>
                }
              />
              <FormControlLabel control={<Checkbox onClick={sortSelected({ allSelected: !selectAll }, false)} checked={selectAll} />} label="Select all" />
              <Badge badgeContent={flaggedNumber} color="primary">
                <FlagIcon className={classes.flagged} />
              </Badge>
            </Box>
            <Box flexGrow="1" />
            <Box display="inline">
              {Object.keys(attributes)
                .filter((attr) => attr !== 'keywords')
                .map((attr, index) => (
                  <FormControlLabel control={<Checkbox onClick={sortSelected({ attrs: { ...attributes, [attr]: !attributes[attr] } })} />} label={snakeToTitle(attr)} key={index} />
                ))}
            </Box>
          </Box>

          <Box display="row-reverse" p={2} mb={2} flexGrow={1}>
            {showConfirmResolve && selected.length > 0 && (
              <Box display="block">
                <Alert severity="warning">Resolve selected?</Alert>
                <Box display="flex" mt={1}>
                  <Button onClick={() => setShowConfirmResolve(false)}>Cancel</Button>
                  <Box flexGrow="1" />
                  <TraxxButton onSubmit={resolve}>Confirm</TraxxButton>
                </Box>
              </Box>
            )}
            {!showConfirmResolve && selected.length > 0 && (
              <Box display="inline" marginLeft={2}>
                <Grid className={classes.inputReason} container spacing={2}>
                  <Grid item md={10} xs={12} sm={10}>
                    <TraxxInput label="Reason for resolution" InputLabelProps={{ shrink: true }} onChange={({ target }) => setResolveReason(target.value)} />
                  </Grid>
                  <Grid item md={2} xs={12} sm={2}>
                    <TraxxButton className={classes.resolveButton} onSubmit={() => setShowConfirmResolve(true)}>
                      Resolve
                    </TraxxButton>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          {matchList.length > 0 && <ListingCompTable headers={header} data={matchList} />}

          {blacklist && (blacklist.some((row) => !row.resolved_at) || showBlacklistResolved) && (
            <>
              <Divider />
              <Box mt={2} mx={1}>
                <Box px={2} py={1} display="flex" alignItems="center">
                  <strong>Blacklist</strong>
                  <Box ml={2}>
                    <FormControlLabel control={<Checkbox checked={showBlacklistResolved} onClick={() => setShowBlacklistResolved((prev) => !prev)} />} label="Show resolved" />
                  </Box>
                </Box>

                {/* resolve controls */}
                <Box p={2}>
                  {showConfirmBlacklistResolve && selectedBlacklist.length > 0 && (
                    <Box display="block">
                      <Alert severity="warning">Resolve selected blacklist entries?</Alert>
                      <Box display="flex" mt={1}>
                        <Button onClick={() => setShowConfirmBlacklistResolve(false)}>Cancel</Button>
                        <Box flexGrow="1" />
                        <TraxxButton onSubmit={resolveBlacklist}>Confirm</TraxxButton>
                      </Box>
                    </Box>
                  )}
                  {!showConfirmBlacklistResolve && selectedBlacklist.length > 0 && (
                    <Grid className={classes.inputReason} container spacing={2}>
                      <Grid item md={10} xs={12} sm={10}>
                        <TraxxInput label="Reason for resolution" InputLabelProps={{ shrink: true }} onChange={({ target }) => setBlacklistResolveReason(target.value)} />
                      </Grid>
                      <Grid item md={2} xs={12} sm={2}>
                        <TraxxButton className={classes.resolveButton} onSubmit={() => setShowConfirmBlacklistResolve(true)}>
                          Resolve
                        </TraxxButton>
                      </Grid>
                    </Grid>
                  )}
                </Box>

                <ListingCompTable
                  headers={['', 'Name', 'Categories', 'Nationality', 'DOB', 'Gender', 'Country of Residence', 'ID Issuing Country', 'Address', 'Information', 'YOB', 'Email', 'Resolved', 'Flag']}
                  data={blacklist
                    .filter((row) => (showBlacklistResolved ? !!row.resolved_at : !row.resolved_at))
                    .map((row) => [
                      {
                        type: 'custom',
                        value: !!row.resolved_at ? <div></div> : <Checkbox checked={selectedBlacklist.includes(row.id)} onClick={() => toggleBlacklistSelect(row.id)} />,
                      },
                      { type: 'custom', value: row.name },
                      {
                        type: 'custom',
                        value: row.categories && row.categories.length > 0 ? <KeywordPills value={row.categories} /> : '-',
                      },
                      { label: row.nationality },
                      { label: row.dob },
                      { label: row.gender },
                      { label: row.countryOfResidence },
                      { label: row.idIssuingCountry },
                      { label: row.address },
                      { label: row.information },
                      { label: row.yob },
                      { label: row.email },
                      { date: row.resolved_at, type: 'date' },
                      {
                        type: 'custom',
                        value: row.flag === 'flagged' ? <FlagIcon style={{ color: 'red' }} /> : '-',
                      },
                    ])}
                />
              </Box>
            </>
          )}
        </Listing>
      )}
    </Paper>
  );
}

export default MatchList;
